import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { CategoryManagementService } from '../../services/category-management.service';
import { AdminSubcategory, FormField } from '../../model/category-management-dto';
import { FieldListComponent } from './field-list/field-list.component';
import { FormPreviewComponent } from './form-preview/form-preview.component';

/**
 * Constructor del formulario dinámico de una subcategoría. Edita el JSON de
 * "fields" (el mismo que renderiza el app móvil) con vista previa en vivo.
 */
@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatTooltipModule,
    FieldListComponent,
    FormPreviewComponent
  ],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss'
})
export class FormBuilderComponent implements OnInit {

  subcategoryId: string;
  subcategory: AdminSubcategory | null = null;
  fields: FormField[] = [];
  showJson = false;
  jsonError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: CategoryManagementService,
    private snackBar: MatSnackBar,
    private spinner: SpinnerVisibilityService
  ) {}

  ngOnInit(): void {
    this.subcategoryId = this.route.snapshot.paramMap.get('subcategoryId') || '';
    this.loadSubcategory();
  }

  loadSubcategory(): void {
    this.spinner.show();
    this.service.getSubcategory(this.subcategoryId).subscribe({
      next: (response) => {
        this.subcategory = response.data;
        this.fields = (response.data?.fields || []).map((f: FormField) => f);
        this.sortByPriority(this.fields);
        this.spinner.hide();
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.snackBar.open('Error cargando el formulario', 'Cerrar', { duration: 3500 });
      }
    });
  }

  onFieldsChange(fields: FormField[]): void {
    this.fields = [...fields];
  }

  get fieldsJson(): string {
    return JSON.stringify({ fields: this.normalizedFields() }, null, 2);
  }

  save(): void {
    if (!this.fields.length) {
      this.snackBar.open('El formulario debe tener al menos un campo', 'Cerrar', { duration: 3500 });
      return;
    }
    const invalid = this.validateFields(this.fields);
    if (invalid) {
      this.snackBar.open(invalid, 'Cerrar', { duration: 4500 });
      return;
    }

    this.spinner.show();
    this.service.updateSubcategory(this.subcategoryId, { fields: this.normalizedFields() }).subscribe({
      next: () => {
        this.spinner.hide();
        this.snackBar.open('Formulario guardado correctamente', 'Cerrar', { duration: 3500 });
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.snackBar.open('Error guardando el formulario', 'Cerrar', { duration: 4000 });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/categories']);
  }

  /**
   * Reasigna las prioridades según el orden actual (también en dependencias)
   * y quita el imageUrl de las opciones: en BD sólo se guarda el fileId y la
   * URL se regenera al leer (las URLs firmadas caducan).
   */
  private normalizedFields(): FormField[] {
    const normalize = (fields: FormField[]): FormField[] =>
      fields.map((field, index) => {
        const copy: FormField = { ...field, priority: index + 1 };
        if (copy.options) {
          copy.options = copy.options.map(({ imageUrl, ...option }) => option);
        }
        if (copy.dependencies) {
          const deps: { [answer: string]: FormField[] } = {};
          Object.entries(copy.dependencies).forEach(([answer, depFields]) => {
            deps[answer] = normalize(depFields);
          });
          copy.dependencies = deps;
        }
        return copy;
      });
    return normalize(this.fields);
  }

  private validateFields(fields: FormField[], path = ''): string | null {
    const names = new Set<string>();
    for (const field of fields) {
      const label = path ? `${path} > ${field.name || '(sin nombre)'}` : (field.name || '(sin nombre)');
      if (!field.name || !field.type) {
        return `El campo "${label}" debe tener nombre y tipo`;
      }
      if (names.has(field.name)) {
        return `Hay campos duplicados con el nombre "${label}"`;
      }
      names.add(field.name);
      if (field.dependencies) {
        for (const [answer, depFields] of Object.entries(field.dependencies)) {
          const error = this.validateFields(depFields, `${label} [${answer}]`);
          if (error) {
            return error;
          }
        }
      }
    }
    return null;
  }

  private sortByPriority(fields: FormField[]): void {
    fields.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
    fields.forEach(field => {
      if (field.dependencies) {
        Object.values(field.dependencies).forEach(dep => this.sortByPriority(dep));
      }
    });
  }
}
